import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile, useIsLargeScreen } from '../../hooks/useResponsive';
import { useAuth } from '../../context/AuthContext';
import { useUrlState } from '../../hooks/useUrlState';
import useAppStore from '../../store/appStore';
import { Inbox as LucideInbox } from 'lucide-react';
import ConversationList from './ConversationList';
import ChatView from './ChatView';
import DetailsPanel from './DetailsPanel';
import BroadcastModal from './BroadcastModal';
import MobileBroadcastModal from './MobileBroadcastModal';
import MobileInbox from './MobileInbox';
import Toast from '../Toast/Toast';
import { onMessagesChange, getLeadDetails, markConversationAsRead } from '../../firebase/conversation.service';

const Inbox = () => {
  const isMobile = useIsMobile();
  const isLargeScreen = useIsLargeScreen();
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  // ── Global store (single source of truth) ─────────────────
  const conversations = useAppStore((s) => s.conversations);
  const cachedMessagesByConvo = useAppStore((s) => s.messagesByConversation);
  const loadingConversations = useAppStore((s) => s.loadingStates.conversations);
  const storeSetMessages = useAppStore((s) => s.setMessages);
  const storeSetActiveConversation = useAppStore((s) => s.setActiveConversation);

  // ── URL state — active conversation ───────────────────────
  const [urlConversation, setUrlConversation] = useUrlState('conversation', '');

  // ── Local UI state ────────────────────────────────────────
  const [activeChat, setActiveChat] = useState(urlConversation || null);
  const [activeLeadData, setActiveLeadData] = useState(null);
  const [messages, setMessages] = useState(
    (urlConversation && cachedMessagesByConvo[urlConversation]) || []
  );
  const [channel, setChannel] = useState('sms');
  const [filter, setFilter] = useState('all');
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingLeadDetails, setLoadingLeadDetails] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('success');

  // Keep a ref to the message-listener unsubscribe so we can
  // clean it up when the active chat changes.
  const msgUnsubRef = useRef(null);

  const handleShowToast = (toastData) => {
    setToastType(toastData.type || 'success');
    setToastMessage(toastData.text);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Derive safe conversations array from store
  const safeConversations = conversations || [];

  // If navigated from another page requesting a specific conversation
  useEffect(() => {
    const openId = location?.state?.openConversationId;
    const channelHint = location?.state?.channelHint;
    if (openId) {
      setActiveChat(openId);
      setUrlConversation(openId);
      storeSetActiveConversation(openId);
      if (channelHint === 'sms' || channelHint === 'email') {
        setChannel(channelHint);
      }
      // Clear location state so back-navigation doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, [location?.state?.openConversationId]); // eslint-disable-line

  // ── Messages: real-time listener with per-conversation caching ──
  useEffect(() => {
    // Clean up previous message listener
    if (msgUnsubRef.current) {
      msgUnsubRef.current();
      msgUnsubRef.current = null;
    }

    if (!activeChat) {
      setMessages([]);
      return;
    }

    // If we have cached messages for this conversation, show them
    // immediately while the real-time listener connects.
    const cached = cachedMessagesByConvo[activeChat];
    if (cached && cached.length > 0) {
      setMessages(cached);
      setLoadingMessages(false);
    } else {
      setLoadingMessages(true);
    }

    // Mark conversation as read when opened
    markConversationAsRead(activeChat);

    // Subscribe to real-time message updates
    const unsubscribe = onMessagesChange(activeChat, (messageList) => {
      setMessages(messageList);
      setLoadingMessages(false);
      // Persist to global store
      storeSetMessages(activeChat, messageList);
    });
    msgUnsubRef.current = unsubscribe;

    return () => {
      if (msgUnsubRef.current) {
        msgUnsubRef.current();
        msgUnsubRef.current = null;
      }
    };
  }, [activeChat]); // eslint-disable-line

  // Load lead details when active chat changes
  useEffect(() => {
    if (!activeChat || !user?.uid) {
      setActiveLeadData(null);
      return;
    }

    const activeChatConvo = safeConversations.find((c) => c.id === activeChat);
    if (!activeChatConvo?.leadId) {
      setActiveLeadData(null);
      return;
    }

    const fetchLeadDetails = async () => {
      setLoadingLeadDetails(true);
      try {
        const leadData = await getLeadDetails(user.uid, activeChatConvo.leadId);
        setActiveLeadData(leadData);
      } catch (error) {
        console.error('Error fetching lead details:', error);
        setActiveLeadData(null);
      } finally {
        setLoadingLeadDetails(false);
      }
    };

    fetchLeadDetails();
  }, [activeChat, user?.uid, safeConversations]);

  const activeChatData = safeConversations.find((c) => c.id === activeChat);

  // Merge conversation data with full lead data for DetailsPanel
  const detailsPanelData = activeChatData ? {
    ...activeChatData,
    ...activeLeadData,
    // Ensure conversation ID is preserved
    id: activeLeadData?.id || activeChatData?.leadId,
  } : null;

  const handleSendMessage = () => {
    if (!messageInput.trim()) return;
    // Message sending will be handled by ChatView component connected to Firebase
    setMessageInput('');
  };

  const openChat = (id) => {
    setActiveChat(id);
    setUrlConversation(id);
    storeSetActiveConversation(id);
  };

  const closeChat = () => {
    setActiveChat(null);
    setUrlConversation('');
    storeSetActiveConversation(null);
  };

  if (isMobile) {
    return (
      <MobileInbox
        conversations={safeConversations}
        showBroadcast={showBroadcast}
        setShowBroadcast={setShowBroadcast}
      />
    );
  }

  return (
    <>
      {toastMessage && <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage(null)} />}
      <div className="flex h-full w-full overflow-hidden text-sm">
        <div className="w-80 border-r border-notion-border bg-white flex flex-col flex-shrink-0 overflow-y-auto">
          <ConversationList
            conversations={safeConversations}
            activeChat={activeChat}
            openChat={openChat}
            filter={filter}
            setFilter={setFilter}
          />
        </div>

        <main className="flex-1 flex flex-col bg-white relative min-w-0">
          {loadingConversations ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-0">
              <p className="text-sm text-stone-500">Loading conversations...</p>
            </div>
          ) : !activeChat ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-0">
              <div className="w-12 h-12 bg-notion-sidebar rounded-lg flex items-center justify-center mb-3">
                <LucideInbox className="w-6 h-6 text-stone-300" />
              </div>
              <p className="text-sm font-medium text-notion-text-light">Select a conversation</p>
            </div>
          ) : (
            <ChatView
              chat={activeChatData}
              messages={messages}
              channel={channel}
              setChannel={setChannel}
              messageInput={messageInput}
              setMessageInput={setMessageInput}
              handleSendMessage={handleSendMessage}
              loading={loadingMessages}
              onToastShow={handleShowToast}
            />
          )}
        </main>

        {isLargeScreen && (
          <aside className="w-80 border-l border-notion-border bg-white flex flex-col flex-shrink-0 overflow-y-auto">
            {!activeChat ? (
              <div className="p-10 flex flex-col items-center justify-center h-full text-center">
                <span className="text-xs text-notion-text-light">Select a lead</span>
              </div>
            ) : loadingLeadDetails ? (
              <div className="p-10 flex flex-col items-center justify-center h-full text-center">
                <span className="text-xs text-notion-text-light">Loading details...</span>
              </div>
            ) : (
              <DetailsPanel chat={detailsPanelData} />
            )}
          </aside>
        )}

        {showBroadcast && <BroadcastModal onClose={() => setShowBroadcast(false)} onToastShow={handleShowToast} />}
      </div>
    </>
  );
};

export default Inbox;
